<template>
  <div>
    <navbar :links="links" />
    <v-layout class="mt-4 mb-2">
      <span class="grey--text text--darken-2 subheading">{{ pageHeader }}</span>
    </v-layout>
    <!-- Loading Indicator -->
    <div
      v-if="loading"
      class="loading-indicator mt-5"
    >
      <v-progress-circular
        :size="50"
        indeterminate
        class="msr--text"
      />
    </div>
    <!-- Stores List -->
    <v-data-table
      v-if="!loading && stores.length"
      ref="table"
      v-model="selectedStores"
      :headers="tableHeaders"
      :items="stores"
      select-all
      :class="{'mobile-table': $vuetify.breakpoint.xsOnly}"
      hide-actions
      must-sort
    >
      <template
        slot="items"
        slot-scope="props"
      >
        <td>
          <v-checkbox
            v-model="props.selected"
            color="msr"
            style="max-width: 40px"
            hide-details
          />
        </td>
        <td>
          <v-layout align-center>
            <v-icon
              v-if="$vuetify.breakpoint.smAndUp"
              medium
              class="msr--text mr-2"
            >
              location_on
            </v-icon>
            <div class="my-2">
              <div class="grey--text text--darken-1">
                {{ props.item.name }}
              </div>
              <div class="subheading grey--text text--darken-3">
                <b>{{ props.item.city }}</b>
                <span class="grey--text">{{ props.item.location }}</span>
              </div>
              <div class="caption grey--text text--darken-1">
                {{ props.item.address }}
              </div>
            </div>
          </v-layout>
        </td>
        <td
          class="body-1 py-1"
          :class="{nowrap: $vuetify.breakpoint.smAndUp}"
        >
          <v-layout
            align-center
            :column="$vuetify.breakpoint.xs"
          >
            <!-- Opening Hours Descriptions -->
            <v-layout
              column
              class="mr-2"
            >
              <div
                v-for="(description, descriptionIndex) in props.item
                  .opening_hours_description_json"
                :key="'description_' + descriptionIndex"
              >
                <v-layout align-center>
                  <div
                    class="text-xs-right mr-3"
                    style="width: 60px"
                  >
                    <strong>{{
                      description.label
                        | translateStoreOpeningHourDescriptionLabel
                    }}</strong>
                  </div>
                  <div class="text-xs-left">
                    {{ description.start }} - {{ description.end }}
                  </div>
                </v-layout>
              </div>
              <div
                v-if="!props.item.opening_hours_description_json.length"
                class="grey--text ml-3 mr-2"
              >
                {{ $t('none') }}
              </div>
            </v-layout>
            <!-- Edit Button for individual Store -->
            <v-btn
              flat
              round
              class="ml-auto mr-2"
              color="msr"
              :disabled="selectedStores.length > 0 || updating"
              :loading="
                selectedStoreIds.indexOf(props.item.id) !== -1 && updating
              "
              @click="selectStoreAndOpenEditDialog(props.item)"
            >
              <v-icon v-if="$vuetify.breakpoint.smAndUp">
                edit
              </v-icon>
              <span v-else>{{ $t('edit') }}</span>
            </v-btn>
          </v-layout>
        </td>
      </template>
    </v-data-table>
    <!-- Edit Button for all selected Stores -->
    <v-slide-x-reverse-transition>
      <v-btn
        v-if="selectedStores.length > 0 && !showEditOpeningHoursDialog"
        :fab="$vuetify.breakpoint.xs"
        :round="$vuetify.breakpoint.smAndUp"
        dark
        fixed
        bottom
        right
        class="msr z-index-3"
        :class="$desktopModeStyles"
        :loading="updating"
        @click.stop="showEditOpeningHoursDialog = true"
      >
        <v-icon :left="$vuetify.breakpoint.smAndUp">
          edit
        </v-icon>
        <span v-if="$vuetify.breakpoint.smAndUp">
          {{ $t('openinghoursEdit') }}
          <span v-if="selectedStores.length > 1">
            ({{
              selectedStores.length === stores.length
                ? $t('all') + ' ' + selectedStores.length
                : selectedStores.length
            }}
            {{ $t('stores') }})
          </span>
        </span>
      </v-btn>
    </v-slide-x-reverse-transition>
    <!-- Edit Opening Hours Dialog -->
    <edit-opening-hours-dialog
      :show.sync="showEditOpeningHoursDialog"
      :loading="updating"
      :stores="selectedStores"
      @aborted="onDialogAborted"
      @confirmed="onDialogConfirmed"
    />
  </div>
</template>

<script>
import i18n from '@/i18n';

import EditOpeningHoursDialog from '@/components/EditOpeningHoursDialog';
import Navbar from '@/components/Navbar';

export default {
  components: {
    EditOpeningHoursDialog,
    Navbar,
  },
  filters: {
    /**
     * Replace BabelEdit keys in label by a short translated name.
     *
     * E.g.: 'weekdayByIndex_0 - weekdayByIndex_2'
     * becomes: 'Mo - Mi' (when german is the selected language).
     */
    translateStoreOpeningHourDescriptionLabel: function (label) {
      for (let i = 0; i <= 6; i++) {
        label = label.replace(
          'weekdayByIndex_' + i,
          i18n.t('weekdayByIndex_' + i).slice(0, 2)
        );
      }
      return label;
    },
  },
  data() {
    const siteHeaderText = this.$t('site');
    const openingHoursText = this.$t('openinghours');

    return {
      links: ['dashboard', 'openinghours'],
      loading: false,
      updating: false,
      tableHeaders: [
        {
          text: siteHeaderText,
          value: 'city',
          sortable: true,
        },
        {
          text: openingHoursText,
          value: 'descriptions',
          sortable: false,
        },
      ],
      stores: [],
      selectedStores: [],
      showEditOpeningHoursDialog: false,
    };
  },
  computed: {
    pageHeader() {
      const num = this.stores.length || '';
      return this.$t('openinghoursPageHeader').replace('{{ num }}', num);
    },
    selectedStoreIds() {
      return this.selectedStores.map((store) => store.id);
    },
  },
  created() {
    this.fetchStores();
  },
  methods: {
    resetSelection() {
      this.selectedStores = [];
    },
    selectStoreAndOpenEditDialog(store) {
      store.selected = true;
      this.selectedStores = [store];

      this.showEditOpeningHoursDialog = true;
    },
    fetchStores() {
      this.loading = true;
      this.$api
        .listStoresForOpeningHours()
        .then((response) => {
          this.stores = response.data;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onDialogAborted() {
      this.resetSelection();
      this.showEditOpeningHoursDialog = false;
    },
    /**
     * Receive weekday objects with start and end fields and send them to the API.
     */
    onDialogConfirmed(weekdays) {
      const payload = {};
      for (let weekday of weekdays) {
        payload['opening_hours_' + weekday.attribute + '_start'] =
          weekday.start;
        payload['opening_hours_' + weekday.attribute + '_end'] = weekday.end;
      }

      this.updating = true;
      this.$api
        .batchUpdateStoreOpeningHours(this.selectedStoreIds, payload)
        .then(() => {
          this.$bus.$emit('alert-success', {
            message: 'openinghoursUpdatedSuccess',
            translate: true,
          });

          this.showEditOpeningHoursDialog = false;
          this.resetSelection();

          this.fetchStores();
        })
        .catch((error) => {
          console.error(error);

          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        })
        .finally(() => {
          this.updating = false;
        });
    },
  },
};
</script>

<style>
@media (max-width: 599px) {
  .mobile-table th {
    white-space: normal !important;
    overflow: hidden !important;
  }

  .mobile-table th,
  td {
    padding: 4px 8px !important;
  }

  .mobile-table thead > tr > th:nth-child(2),
  td.days {
    max-width: 40px !important;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/OpeningHours.vue